import { useState } from 'react'
import { useSsoToken } from './useSsoToken'
import { addAdhocContact, ApiValidationError } from '../services/claimsApi'
import { getApiErrorMessage, DUPLICATE_ADHOC_CONTACT_MESSAGE } from '../utils/errorMessages'

// How the proc words a repeated name+email pair (Msg 105). The form pre-checks the pair
// against the claim's contacts, so this only fires when that list was out of date — the
// proc is the authority, and its jargon is swapped for the form's own message.
const DUPLICATE_PAIR_PATTERN = /combo already exists/i

// A rule the proc enforced (400) rather than a transport failure: retrying the same values
// can't succeed, so the reason is shown instead of "please try again".
function getSaveErrorMessage(err: unknown): string {
  if (err instanceof ApiValidationError) {
    if (DUPLICATE_PAIR_PATTERN.test(err.message)) return DUPLICATE_ADHOC_CONTACT_MESSAGE
    return err.message
  }
  return getApiErrorMessage(err, 'Failed to add contact. Please try again.')
}

// Saves an unmatched email recipient to MC1 as an ad-hoc contact (MSD-256).
// `save` resolves to true on success so callers can collapse the form and refresh.
export function useAddAdhocContact(claimNumber: string) {
  const { getToken } = useSsoToken()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async (name: string, email: string): Promise<boolean> => {
    setSaving(true)
    setError(null)
    try {
      const token = await getToken()
      await addAdhocContact(claimNumber, { adHocName: name, adHocEmail: email }, token)
      return true
    } catch (err: unknown) {
      console.error('[AddAdhocContact] Failed to save contact:', err)
      setError(getSaveErrorMessage(err))
      return false
    } finally {
      setSaving(false)
    }
  }

  return { saving, error, save }
}
